import { addHours, format, parseISO, startOfDay } from "date-fns";
import type { DateRange } from "react-day-picker";

export const DEFAULT_PICKUP_TIME = "10:00";
export const DEFAULT_RETURN_TIME = "10:00";
export const DEFAULT_DRIVER_AGE = "30";

/** Ad-landing default: pickup today+24h, return today+96h (calendar days @ 10:00). */
export const DEFAULT_PICKUP_OFFSET_HOURS = 24;
export const DEFAULT_RETURN_OFFSET_HOURS = 96;

export const SEARCH_TIME_OPTIONS = [
  "08:00",
  "08:30",
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
  "18:30",
  "19:00",
  "19:30",
  "20:00",
] as const;

export const DRIVER_AGE_OPTIONS = [
  { value: "21", labelKey: "search.driverAge.under25" },
  { value: "25", labelKey: "search.driverAge.from25" },
  { value: "30", labelKey: "search.driverAge.from30" },
] as const;

export const parseDateRangeFromUrl = (datesParam: string): DateRange | undefined => {
  if (!datesParam) return undefined;
  const parts = datesParam.split("|");
  if (parts.length !== 2) return undefined;
  try {
    const from = parseISO(parts[0]);
    const to = parseISO(parts[1]);
    if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
      return { from, to };
    }
  } catch {
    return undefined;
  }
  return undefined;
};

/**
 * Default search dates for empty URL state:
 * pickup = start of (now + 24h), return = start of (now + 96h).
 * Times stay at 10:00 via DEFAULT_PICKUP_TIME / DEFAULT_RETURN_TIME.
 */
export const getDefaultSearchDateRange = (now: Date = new Date()): DateRange => {
  const pickup = startOfDay(addHours(now, DEFAULT_PICKUP_OFFSET_HOURS));
  const returnDate = startOfDay(addHours(now, DEFAULT_RETURN_OFFSET_HOURS));
  return { from: pickup, to: returnDate };
};

/**
 * Resolve dates from ad/landing URL params.
 * Supports:
 * - `dates=YYYY-MM-DD|YYYY-MM-DD` (canonical)
 * - `pickup` / `return` (ad aliases)
 * Falls back to default range when neither is present (unless `allowEmpty`).
 */
export const resolveDateRangeFromSearchParams = (
  searchParams: URLSearchParams,
  options?: { allowEmpty?: boolean; now?: Date }
): DateRange | undefined => {
  const datesParam = searchParams.get("dates")?.trim() || "";
  const fromDates = parseDateRangeFromUrl(datesParam);
  if (fromDates?.from && fromDates?.to) return fromDates;

  const pickupRaw =
    searchParams.get("pickup")?.trim() || searchParams.get("start")?.trim() || "";
  const returnRaw =
    searchParams.get("return")?.trim() || searchParams.get("end")?.trim() || "";

  if (pickupRaw && returnRaw) {
    try {
      const from = parseISO(pickupRaw);
      const to = parseISO(returnRaw);
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
        return { from, to };
      }
    } catch {
      // fall through to default
    }
  }

  if (options?.allowEmpty) return undefined;
  return getDefaultSearchDateRange(options?.now);
};

/** Prefer location hubs from ads: location, city, or nested pickup hub slugs. */
export const resolveInitialLocation = (
  searchParams: URLSearchParams,
  defaultLocation?: string
): string => {
  const fromUrl =
    searchParams.get("location")?.trim() ||
    searchParams.get("city")?.trim() ||
    searchParams.get("pickup_hub")?.trim() ||
    "";
  if (fromUrl) return fromUrl;
  return defaultLocation?.trim() || "";
};

export const resolveReturnLocation = (
  searchParams: URLSearchParams,
  pickupLocation: string
): string => {
  const fromUrl =
    searchParams.get("returnLocation")?.trim() ||
    searchParams.get("dropoff")?.trim() ||
    "";
  return fromUrl || pickupLocation;
};

export const resolveTimeParam = (
  searchParams: URLSearchParams,
  key: "pickupTime" | "returnTime",
  fallback: string
): string => {
  const value = searchParams.get(key)?.trim();
  if (value && /^\d{1,2}:\d{2}$/.test(value)) return value;
  return fallback;
};

export const resolveDriverAge = (searchParams: URLSearchParams): string => {
  const value = searchParams.get("driverAge")?.trim();
  if (value && DRIVER_AGE_OPTIONS.some((option) => option.value === value)) {
    return value;
  }
  return DEFAULT_DRIVER_AGE;
};

export interface SearchQueryInput {
  location: string;
  dateRange?: DateRange;
  returnLocation?: string;
  differentReturnLocation?: boolean;
  pickupTime?: string;
  returnTime?: string;
  driverAge?: string;
}

export const buildSearchUrl = ({
  location,
  dateRange,
  returnLocation,
  differentReturnLocation,
  pickupTime,
  returnTime,
  driverAge,
}: SearchQueryInput): string => {
  const params = new URLSearchParams();
  params.set("location", location);

  if (dateRange?.from && dateRange?.to) {
    const formattedStartDate = format(dateRange.from, "yyyy-MM-dd");
    const formattedEndDate = format(dateRange.to, "yyyy-MM-dd");
    params.set("dates", `${formattedStartDate}|${formattedEndDate}`);
  }

  if (
    differentReturnLocation &&
    returnLocation?.trim() &&
    returnLocation.trim() !== location.trim()
  ) {
    params.set("returnLocation", returnLocation.trim());
  }

  if (pickupTime) params.set("pickupTime", pickupTime);
  if (returnTime) params.set("returnTime", returnTime);
  if (driverAge) params.set("driverAge", driverAge);

  return `/search?${params.toString()}`;
};
